'use client';

import React, { useId } from 'react';
import { composeTailwindRenderProps } from '@utils';
import {
	NumberField as AriaNumberField,
	NumberFieldProps as AriaNumberFieldProps,
	ValidationResult,
} from 'react-aria-components';

import { skipChangeByPatternInvalid as avoidChangeByPatternInvalid } from '@/utils/fieldHelper';
import {
	Description,
	FieldError,
	FieldGroup,
	FieldSuccess,
	FieldVariant,
	FieldWidth,
	iconStyle,
	Input,
	Label,
} from './Field';

export interface NumberFieldProps extends AriaNumberFieldProps {
	label?: string;
	hint?: string;
	hintChildren?: React.ReactNode;
	successMessage?: string;
	errorMessage?: string | ((validation: ValidationResult) => string);
	isValid?: boolean;
	placeholder?: string;
	theme?: 'light' | 'dark';
	leftIcon?: React.ReactElement<{ className?: string }>;
	rightIcon?: React.ReactElement<{ className?: string }>;
	variant?: FieldVariant;
	width?: FieldWidth;
	pattern?: string | RegExp;
	isWheelDisabled?: boolean;
}

export function NumberField({
	label,
	hint,
	hintChildren,
	errorMessage,
	isValid,
	successMessage,
	theme,
	placeholder,
	leftIcon,
	rightIcon,
	variant,
	width,
	pattern,
	isWheelDisabled = true,
	...props
}: NumberFieldProps) {
	const id = useId();

	const handleOnBeforeInput = (e: React.FormEvent<HTMLInputElement>) =>
		pattern ? avoidChangeByPatternInvalid(e, pattern) : undefined;

	return (
		<AriaNumberField
			id={id}
			{...props}
			isWheelDisabled={isWheelDisabled}
			className={composeTailwindRenderProps(props.className, 'flex flex-col gap-2')}
		>
			{({ isInvalid, isDisabled }) => (
				<>
					{label && <Label isRequired={props.isRequired}>{label}</Label>}
					{hint && <Description>{hint}</Description>}
					{hintChildren && hintChildren}
					{errorMessage && <FieldError>{errorMessage}</FieldError>}
					{isValid && successMessage && <FieldSuccess>{successMessage}</FieldSuccess>}
					<FieldGroup
						theme={theme}
						variant={variant}
						width={width}
						isValid={isValid}
						isInvalid={isInvalid}
						isDisabled={isDisabled}
					>
						{leftIcon &&
							React.cloneElement(leftIcon, {
								className: iconStyle({
									theme,
									isInvalid,
									isDisabled,
									isValid,
								}),
							})}
						<Input placeholder={placeholder} theme={theme} onBeforeInput={handleOnBeforeInput} />
						{rightIcon &&
							React.cloneElement(rightIcon, {
								className: iconStyle({
									theme,
									isInvalid,
									isDisabled,
									isValid,
								}),
							})}
					</FieldGroup>
				</>
			)}
		</AriaNumberField>
	);
}
